import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Star, ShieldCheck, Clock, ThumbsUp, Truck, Home as HomeIcon, Building2, Boxes, Warehouse, Sofa, Trash2, MapPinned, Globe } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import TrustBar from '../components/TrustBar';
import QuoteForm from '../components/QuoteForm';
import SEO from '../components/SEO';
import { BUSINESS, SERVICES, REVIEWS, FAQS, PROCESS } from '../data/content';
import { Reveal } from '../hooks/useReveal';

const ICONS = { Home: HomeIcon, Truck, Building2, Boxes, Warehouse, Sofa, Trash2, MapPinned, Globe };

const HERO_IMG = 'https://images.unsplash.com/photo-1758523670991-ee93bc48d81d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxtb3ZlcnMlMjBjYXJyeWluZyUyMGJveGVzJTIwaG9tZXxlbnwwfHx8fDE3ODEwNDI1NjB8MA&ixlib=rb-4.1.0&q=85';

export default function Home() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'MovingCompany',
        '@id': 'https://laraelogistics.com/#org',
        name: 'Laraé Logistics',
        image: HERO_IMG,
        telephone: BUSINESS.phone,
        email: BUSINESS.email,
        address: { '@type': 'PostalAddress', addressLocality: 'London', addressRegion: 'Greater London', addressCountry: 'GB' },
        areaServed: ['London', 'Greater London', 'Home Counties', 'United Kingdom'],
        priceRange: '££',
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQS.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <SEO title="Laraé Logistics — Removals & Man and Van London + Nationwide UK | Fully Insured"
        description="Professional, fully insured removals, man and van, office relocations and long-distance moves across London and the UK. No deposit required. Reply within 10 minutes during working hours. Call 020 3857 7761."
        canonical={typeof window !== 'undefined' ? window.location.origin + '/' : ''}
        jsonLd={jsonLd} />

      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden bg-forest text-white">
        <div className="absolute inset-0 grain"></div>
        <img src={HERO_IMG} alt="Movers carrying boxes" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest/95 via-forest/85 to-forest/70"></div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 pt-16 md:pt-24 lg:pt-32 pb-20 md:pb-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <ShieldCheck size={14} className="text-oat" />
              <span className="font-manrope text-xs font-medium tracking-wide">Fully Insured · £1M Public Liability</span>
            </div>
            <h1 data-testid="hero-headline" className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight">
              Professional removals across<br />London &amp; nationwide UK.
            </h1>
            <p className="font-manrope text-base md:text-lg text-white/80 mt-6 max-w-2xl leading-relaxed">
              Reliable, fully insured removals and transport for homes and businesses. House moves, man &amp; van, office relocations, and long-distance moves anywhere in the UK. Fixed quotes, careful crews, on-time arrivals.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/quote" data-testid="hero-quote-btn" className="bg-oat hover:bg-oat/90 text-ink font-semibold rounded-full px-7 py-4 text-base inline-flex items-center justify-center gap-2 transition-all">
                Get Free Quote <ArrowRight size={18} />
              </Link>
              <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="hero-call-btn" className="bg-white/10 border border-white/30 hover:bg-white/20 text-white font-medium rounded-full px-7 py-4 text-base inline-flex items-center justify-center gap-2 backdrop-blur">
                <Phone size={18} /> {BUSINESS.phone}
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-10 text-sm text-white/70 font-manrope">
              <div className="flex items-center gap-1.5"><ShieldCheck size={14} /> Fully insured · £1M cover</div>
              <div className="flex items-center gap-1.5"><Clock size={14} /> Same-day availability</div>
              <div className="flex items-center gap-1.5"><ThumbsUp size={14} /> Family-run since day one</div>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* SERVICES */}
      <section data-testid="services-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="max-w-2xl mb-12">
            <p className="font-manrope text-sm font-medium text-forest uppercase tracking-widest mb-3">What we do</p>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium text-ink tracking-tight">Removals services for every kind of move.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.slice(0, 8).map((s, i) => {
              const Icon = ICONS[s.icon] || Truck;
              return (
                <Reveal key={s.slug} delay={i * 60}>
                  <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                    className="group block h-full bg-white border border-sand rounded-2xl p-6 hover:border-forest hover:shadow-md hover:-translate-y-0.5 transition-all">
                    <div className="w-12 h-12 rounded-xl bg-sand grid place-items-center mb-5 group-hover:bg-forest group-hover:text-white transition-colors text-forest">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-outfit text-lg font-semibold text-ink mb-2">{s.title}</h3>
                    <p className="font-manrope text-sm text-ink/65 leading-relaxed">{s.short}</p>
                    <div className="flex items-center gap-1.5 text-forest font-manrope text-sm font-medium mt-4 group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={14} /></div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-sand/50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-manrope text-sm font-medium text-forest uppercase tracking-widest mb-3">Why Laraé</p>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium text-ink tracking-tight">A removals company built on trust.</h2>
            <p className="font-manrope text-base text-ink/70 mt-5 leading-relaxed">
              We're a family-run team that treats every move like our own. No call centres, no inflated quotes — just experienced movers, honest pricing, and a service you'd recommend to a friend.
            </p>
            <div className="grid grid-cols-2 gap-5 mt-8">
              {[
                { n: '£1M', l: 'Public liability cover' },
                { n: '£10k', l: 'Goods in transit cover' },
                { n: '10 min', l: 'Average response time' },
                { n: 'UK-wide', l: 'London & nationwide' },
              ].map(s => (
                <div key={s.l} className="bg-white rounded-2xl p-5 border border-sand">
                  <div className="font-outfit text-2xl md:text-3xl font-semibold text-forest">{s.n}</div>
                  <div className="font-manrope text-sm text-ink/60 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="aspect-[4/5] rounded-3xl overflow-hidden">
            <img src="https://images.unsplash.com/photo-1614359835514-92f8ba196357?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBtb3ZpbmclMjB2YW4lMjBkZWxpdmVyeXxlbnwwfHx8fDE3ODEwNDI1NjB8MA&ixlib=rb-4.1.0&q=85" alt="Professional moving van" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="max-w-2xl mb-12">
            <p className="font-manrope text-sm font-medium text-forest uppercase tracking-widest mb-3">How it works</p>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium text-ink tracking-tight">Booking a move is simple.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 100}>
                <div className="relative">
                  <div className="font-outfit text-5xl font-medium text-oat/40">{p.step}</div>
                  <h3 className="font-outfit text-xl font-semibold text-ink mt-2">{p.title}</h3>
                  <p className="font-manrope text-sm text-ink/65 mt-2 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section data-testid="reviews-section" className="bg-forest text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-5 mb-12">
            <div>
              <p className="font-manrope text-sm font-medium text-oat uppercase tracking-widest mb-3">Customer reviews</p>
              <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">What people say about us.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REVIEWS.slice(0, 6).map((r, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur h-full hover:bg-white/10 hover:-translate-y-0.5 transition-all">
                  <div className="flex mb-3">{[1,2,3,4,5].map(s=><Star key={s} size={14} className="fill-oat text-oat" />)}</div>
                  {r.service && <div className="inline-block text-[10px] font-manrope uppercase tracking-widest text-oat/90 bg-oat/10 border border-oat/20 rounded-full px-2.5 py-1 mb-3">{r.service}</div>}
                  <p className="font-manrope text-sm text-white/85 leading-relaxed">"{r.text}"</p>
                  <div className="mt-5 font-manrope text-sm font-medium text-white">{r.name} <span className="text-white/50 font-normal">· {r.area}</span></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVING LONDON & THE UK — clean coverage block */}
      <section data-testid="coverage-section" className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <p className="font-manrope text-sm font-medium text-forest uppercase tracking-widest mb-3">Serving London &amp; the UK</p>
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium text-ink tracking-tight">
            Professional removals across London<br className="hidden md:block" /> and throughout the UK.
          </h2>
          <p className="font-manrope text-base text-ink/65 mt-5 max-w-2xl mx-auto leading-relaxed">
            We provide professional removals services throughout London, Greater London and nationwide destinations across the UK.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-10 max-w-4xl mx-auto">
            {[
              { label: 'All London Boroughs', to: '/coverage/london-boroughs' },
              { label: 'Greater London', to: '/coverage/greater-london' },
              { label: 'Home Counties', to: '/coverage/home-counties' },
              { label: 'Long Distance Moves', to: '/coverage/long-distance-moves' },
              { label: 'Nationwide Relocations', to: '/coverage/nationwide-relocations' },
            ].map(c => (
              <Link key={c.to} to={c.to} className="bg-white border border-sand rounded-xl px-3 py-3 hover:border-forest hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 text-sm font-manrope font-medium text-ink">
                <span className="text-forest">✓</span> {c.label}
              </Link>
            ))}
          </div>
          <div className="mt-9">
            <Link to="/quote" data-testid="coverage-cta" className="inline-flex items-center gap-2 bg-forest hover:bg-forest/90 text-white font-semibold rounded-full px-7 py-4 transition-all hover:translate-x-0.5">
              Get My Free Quote <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section data-testid="home-quote-section" className="bg-sand/50 py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="font-manrope text-sm font-medium text-forest uppercase tracking-widest mb-3">Get a quote</p>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium text-ink tracking-tight">Tell us about your move.</h2>
            <p className="font-manrope text-base text-ink/65 mt-4">Takes under 60 seconds. We typically reply within 10 minutes during working hours.</p>
            <p className="font-manrope text-xs text-ink/50 mt-3 italic max-w-md mx-auto">Popular moving dates can fill up quickly — particularly weekends and month-end. Request your quote early to secure availability.</p>
          </div>
          <QuoteForm sourcePage="home" />
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-10">
            <p className="font-manrope text-sm font-medium text-forest uppercase tracking-widest mb-3">FAQs</p>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium text-ink tracking-tight">Frequently asked questions.</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-sand">
                <AccordionTrigger className="font-outfit text-base md:text-lg font-medium text-ink hover:text-forest hover:no-underline text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="font-manrope text-sm md:text-base text-ink/70 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-forest text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">Ready when you are.</h2>
          <p className="font-manrope text-base text-white/75 mt-4 max-w-xl mx-auto">Call us today or send the form — we typically reply within 10 minutes during working hours.</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-7 justify-center">
            <a href={`tel:${BUSINESS.phoneRaw}`} className="bg-oat hover:bg-oat/90 text-ink font-semibold rounded-full px-7 py-4 inline-flex items-center justify-center gap-2"><Phone size={18} /> {BUSINESS.phone}</a>
            <Link to="/quote" className="bg-white/10 border border-white/30 hover:bg-white/20 text-white font-medium rounded-full px-7 py-4 inline-flex items-center justify-center gap-2">Free Quote <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
